import * as React from 'react'
import { ethers } from 'ethers'
import { guestPortfolioPath, PageProps, ToastTypes } from '@logic'
import { PageContent } from '@components/pages'
import { FlexRow, screenSizes } from '@src/styles'
import { Tabs } from '@shared/components/tabs/tabs'
import { getNameForAddress, guestWalletTabs, walletTabs } from '../utility'
import { SubmitButton } from '@shared/components/button'
import { connectWalletModal } from '../components/modal/connect-wallet-modal'
import {
    deleteWalletAddress,
    editUserState,
    editWalletAddress,
    EditWalletAddressRequest,
    globalDisplayToast,
    newWalletAddress,
    NewWalletAddressRequest
} from '@src/service'
import { dynamicAddField } from '@shared/components/dynamic-add-field'
import { ellipsesColumn, StyledTable, Table } from '@shared/components/tables'
import styled from 'styled-components'
import { basicDeleteModal } from '@shared/components/modal'
import { newEditAddressModal } from '../components/modal/edit-wallet-address'
import { successModal } from '../components/modal/success-modal'
import { BetaNotify } from '../pages'

const AddressBookWrap = styled.div<any>`
    ${StyledTable} {
        max-width: 845px !important;
        margin-top: 24px;
    }
    table {
        min-width: 100%;
        thead tr {
            background-color: rgba(255,255,255,0.05) !important;
        }
        th, td {
            border-right: 0;
        }
        tr {
            td {
                font-weight: normal !important;
                font-family: NunitoSans !important;
            }
        }
    }
`

const StyledAddRow = styled<any>(FlexRow)`
    width: 100%;
    max-width: 758px;
    .connectButton {
        margin-left: 16px;
    }
    @media (max-width: ${screenSizes.S}px) {
        flex-direction: column;
        .connectButton {
            margin: 16px 0 0 0;
        }
    }
`

type Props = PageProps & { forGuest?: boolean, openSignUp?: () => void }

export const WalletAddressComponent = (props: Props) => {
    const [installed, setInstalled] = React.useState<boolean>(false)
    const [selectedAddress, setSelectedAddress] = React.useState<any>()
    const { params, httpClient, user, reload, forGuest, openSignUp, navigate } = props
    const records = props?.walletAddressRecords || []
    const userState = props?.userStateRecords

    const tableData = records ? records.map((r: any) => {
        const selectedName = getNameForAddress(r.name, r.globalName)
        const isAddress = selectedName.match(/^0x[a-fA-F0-9]{40}$/)
        const abbName = isAddress ?
            selectedName.slice(0, 6) + '...' + selectedName.slice(selectedName.length - 4, selectedName.length)
            : selectedName
        return { ...r, abbName }
    }) : []

    const addField = dynamicAddField({
        placeholder: 'Enter address...',
        onClick: forGuest ? openSignUp : () => addUser([addField.inputValue], true)
    })
    const AddField = addField.component
    const closeSuccessModal = () => {
        successModalBundle.setVisible(false)

        if (!forGuest) reload()
        else navigate(guestPortfolioPath)
    }
    const successModalBundle = successModal({ onActivate: () => closeSuccessModal() })

    const addUser = async (accounts: any, get?: boolean) => {
        const { ethereum } = window as any
        if (accounts.length > 0) {
            let ensName = accounts[0]
            if (ethereum) {
                try {
                    const provider = new ethers.providers.Web3Provider(ethereum)
                    ensName = await provider.lookupAddress(accounts[0]) || accounts[0]
                } catch (e) {
                    console.error('ENS lookup failed:', e)
                }
            }
            const addressRequest: NewWalletAddressRequest = {
                entity: user?.id || '',
                address: accounts[0],
                name: accounts[0],
                globalName: ensName
            }
            const res = await newWalletAddress(httpClient)({}, addressRequest)
            if (res) globalDisplayToast(ToastTypes.success, 'Address successfully added to address book')
            if (get && reload) reload()
        }
    }

    const onClickConnect = async () => {
        const { ethereum } = window as any
        if (!ethereum) {
            console.error('Ethereum provider not found')
            return
        }
        try {
            if (userState && userState.walletUser) {
                await ethereum.request({
                    method: "wallet_requestPermissions",
                    params: [
                        {
                            eth_accounts: {}
                        }
                    ]
                })
            }
            else await ethereum.request({ method: 'eth_requestAccounts' })
            const accounts = await ethereum.request({ method: 'eth_accounts' })
            if (accounts.length > 0) {
                if (!forGuest) {
                    await editUserState(httpClient)({}, { userState: { ...userState, walletUser: accounts[0] } })
                    addUser(accounts)
                }
                connectWalletBundle.setVisible(false)
                successModalBundle.setVisible(true)
            }
            else connectWalletBundle.setVisible(false)
        } catch (error) {
            console.error(error)
        }
    }

    const connectWalletBundle = connectWalletModal({ installed, onActivate: onClickConnect })

    const initialize = () => {
        const isMetaMaskInstalled = () => {
            const { ethereum } = window as any
            return Boolean(ethereum && ethereum.isMetaMask)
        }

        const metaMaskClientCheck = () => {
            if (!isMetaMaskInstalled()) setInstalled(false)
            else setInstalled(true)
            connectWalletBundle.setVisible(true)
        }
        metaMaskClientCheck()
    }

    const handleDeleteAddress = async () => {
        await deleteWalletAddress(httpClient)({ entity: selectedAddress?.entity, address: selectedAddress?.address })
        deleteModal.setVisible(false)
        reload()
    }

    const deleteModal = basicDeleteModal(
        handleDeleteAddress,
        [selectedAddress?.address || ''],
        '', '',
        'You are about to delete the following item from your address book:'
    )

    const editAddress = async (values: any) => {
        const editAddressRequest = {
            entity: selectedAddress?.entity,
            address: values.address,
            name: values.name,
            globalName: selectedAddress?.globalName
        } as EditWalletAddressRequest
        const response = await editWalletAddress(httpClient)({ wallet: selectedAddress?.id }, editAddressRequest)
        if (response) {
            editAddressModal.setVisible(false)
            reload()
        }
    }

    const editAddressModal = newEditAddressModal(
        values => editAddress(values),
        {
            name: getNameForAddress(selectedAddress?.name, selectedAddress?.globalName),
            address: selectedAddress?.address || '',
        }
    )

    const columns = [
        {
            Header: 'Name',
            accessor: 'abbName',
        },
        {
            Header: 'Address',
            accessor: 'address',
            className: 'left'
        },
        ellipsesColumn(
            [
                ['Edit', ({ row }) => { setSelectedAddress(row.original), editAddressModal.setVisible(true) }],
                ['Delete', ({ row }) => { setSelectedAddress(row.original), deleteModal.setVisible(true) }]
            ]
        ),
    ]

    // ethereum?.on('accountsChanged', async (accounts: any) => {
    //     const accts = await ethereum.enable();
    //     await editUserState(httpClient)({}, { userState: { ...userState, walletUser: accts[0] } })
    //     addUser(accts, true)
    // })

    return (
        <>
            {editAddressModal.component}
            {deleteModal.component}
            {connectWalletBundle.component}
            {successModalBundle.component}
            <Tabs tabs={forGuest ? guestWalletTabs : walletTabs} title={
                <>
                    Wallets
                    <BetaNotify>Beta</BetaNotify>
                </>
            } params={params} />
            <PageContent withMargin={forGuest}>
                <AddressBookWrap>
                    <StyledAddRow alignItems={'flex-start'}>
                        {AddField}
                        <SubmitButton
                            id={'connectButton'}
                            className='connectButton'
                            onClick={() => initialize()}>CONNECT A WALLET</SubmitButton>
                    </StyledAddRow>
                    <Table
                        columns={columns}
                        data={tableData}
                        description={tableData.length > 0 ? undefined : <span>Your address book is empty.</span>}
                        noFooter={tableData.length > 0 ? false : true}
                    />
                </AddressBookWrap>
            </PageContent>
        </>
    )
}
